"use client"

import * as React from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faAngleDown, faCheck } from "@instadeepai/fontawesome-pro-regular"
import * as ScrollAreaPrimitive from "@radix-ui/react-scroll-area"
import * as SelectPrimitive from "@radix-ui/react-select"

import { cn } from "../../lib/utils"

const Select = SelectPrimitive.Root

const SelectGroup = SelectPrimitive.Group

const SelectValue = SelectPrimitive.Value

export interface SelectProps
  extends React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger> {
  leftIcon?: React.ReactElement<{ className?: string }>
  rightIcon?: React.ReactElement<{ className?: string }>
}

const SelectTrigger = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Trigger>,
  SelectProps
>(({ className, rightIcon, leftIcon, children, ...props }, ref) => (
  <SelectPrimitive.Trigger
    ref={ref}
    className={cn(
      "flex h-10 w-full items-center justify-between gap-[50px] rounded-md border border-gray-200 bg-gradient-to-b from-white to-gray-50 px-[18px] py-[10px] text-sm font-medium not-italic leading-6 text-gray-700 shadow-[inset_0px_2px_2px_0px_gray-100] hover:border-primary-500 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50 data-[state=open]:border-primary-500",
      className
    )}
    {...props}
  >
    <span className="flex items-center gap-2">
      {leftIcon && (
        <SelectPrimitive.Icon asChild>
          {React.cloneElement(leftIcon, { className: "w-3.5" })}
        </SelectPrimitive.Icon>
      )}
      {children}
    </span>

    {rightIcon ? (
      <SelectPrimitive.Icon asChild>
        {React.cloneElement(rightIcon, { className: "w-3.5" })}
      </SelectPrimitive.Icon>
    ) : (
      <FontAwesomeIcon icon={faAngleDown} className="w-3.5" />
    )}
  </SelectPrimitive.Trigger>
))
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName

const SelectContent = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content>
>(({ className, children, position = "popper", ...props }, ref) => (
  <SelectPrimitive.Portal>
    <SelectPrimitive.Content
      ref={ref}
      className={cn(
        "relative z-50 h-fit max-h-96 min-w-32 rounded-md border bg-white shadow-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
        position === "popper" &&
          "data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1",
        className
      )}
      position={position}
      {...props}
    >
      <ScrollAreaPrimitive.Root className="size-full p-1" type="auto">
        <SelectPrimitive.Viewport
          className={cn(
            "pr-2",
            position === "popper" &&
              "h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)]"
          )}
          asChild
        >
          {/* a workaround for https://github.com/radix-ui/primitives/issues/2059 */}
          <ScrollAreaPrimitive.Viewport className="!size-full" style={{ overflowY: undefined }}>
            {children}
          </ScrollAreaPrimitive.Viewport>
        </SelectPrimitive.Viewport>
        <ScrollAreaPrimitive.Scrollbar
          className="mr-0.5 w-2 py-1"
          orientation="vertical"
        >
          <ScrollAreaPrimitive.Thumb className="rounded-md bg-gray-200" />
        </ScrollAreaPrimitive.Scrollbar>
      </ScrollAreaPrimitive.Root>
    </SelectPrimitive.Content>
  </SelectPrimitive.Portal>
))
SelectContent.displayName = SelectPrimitive.Content.displayName

const SelectLabel = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Label>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Label
    ref={ref}
    className={cn("px-2 py-1.5 text-md font-semibold", className)}
    {...props}
  />
))
SelectLabel.displayName = SelectPrimitive.Label.displayName

const SelectItem = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item>
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Item
    ref={ref}
    className={cn(
      "flex w-full cursor-pointer select-none items-center justify-between rounded-sm py-2.5 pl-2 pr-2.5 outline-none focus:bg-gray-50 data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
      className
    )}
    {...props}
  >
    <SelectPrimitive.ItemText>
      <span className="text-md font-medium text-gray-900">{children}</span>
    </SelectPrimitive.ItemText>
    <SelectPrimitive.ItemIndicator>
      <span className="flex items-center justify-center">
        <FontAwesomeIcon icon={faCheck} className="size-5 text-blue-600" />
      </span>
    </SelectPrimitive.ItemIndicator>
  </SelectPrimitive.Item>
))
SelectItem.displayName = SelectPrimitive.Item.displayName

const SelectSeparator = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Separator
    ref={ref}
    className={cn("bg-muted -mx-1 my-1 h-px", className)}
    {...props}
  />
))
SelectSeparator.displayName = SelectPrimitive.Separator.displayName

export {
  Select,
  SelectGroup,
  SelectValue,
  SelectTrigger,
  SelectContent,
  SelectLabel,
  SelectItem,
  SelectSeparator,
}
